import { useMemo } from 'react';
import { safeJsonParse } from '../../../utils/security';
import { schemaToFields } from '../converters/fieldSchemaConverter';
import { annotationsToConfig } from '../converters/annotationsConverter';
import { useConnectionsStore } from '../../../store/connectionsStore';
import InfoLabel from '../../../ui/InfoLabel/InfoLabel';
import {
  CheckCircle2,
  FileText,
  ListChecks,
  ArrowRightLeft,
  MessageCircle,
  ChevronDown,
} from 'lucide-react';
import { useState } from 'react';
import styles from './ReviewStep.module.css';

interface ReviewStepProps {
  watch: any;
  setValue: any;
  register: any;
  errors: any;
  canEdit: boolean;
  onSave?: () => void;
  saving?: boolean;
  isNew?: boolean;
}

export default function ReviewStep({ watch, isNew }: ReviewStepProps) {
  const [expandedJson, setExpandedJson] = useState<string | null>(null);
  const connectors = useConnectionsStore(s => s.connectors);

  const name = watch('name') || '';
  const description = watch('description') || '';
  const mainConnectionId = watch('mainConnectionId');
  const httpMethod = watch('httpMethod') || 'POST';
  const path = watch('path') || '/';
  const tags = watch('tags') || [];
  const enabled = watch('enabled');

  const connector = connectors.find(c => c.id === mainConnectionId);

  const fields = useMemo(() => {
    const raw = watch('inputSchema') || '{}';
    const parsed = safeJsonParse(raw, {});
    return parsed.success ? schemaToFields(parsed.data) : [];
  }, [watch('inputSchema')]);

  const chatConfig = useMemo(() => {
    const raw = watch('annotations') || '{}';
    const parsed = safeJsonParse(raw, {});
    return parsed.success ? annotationsToConfig(parsed.data) : { promptSuggestions: [], missingFieldQuestions: {}, confirmBeforeExecute: false };
  }, [watch('annotations')]);

  const toggleJson = (key: string) => {
    setExpandedJson(expandedJson === key ? null : key);
  };

  return (
    <div className={styles.step}>
      <div className={styles.summaryHeader}>
        <CheckCircle2 size={20} className={styles.checkIcon} />
        <div>
          <div className={styles.summaryTitleRow}>
            <h3 className={styles.summaryTitle}>Ready to {isNew ? 'create' : 'save'}</h3>
            <InfoLabel content="Review your tool configuration before saving." />
          </div>
        </div>
      </div>

      {/* 1. Tool Info */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <FileText size={16} />
          <span className={styles.cardTitle}>1. Tool Info</span>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Name</span>
            <span className={styles.infoValue}>{name || '-'}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Description</span>
            <span className={styles.infoValue}>{description || '-'}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Connection</span>
            <span className={styles.infoValue}>{connector?.connectionName || '-'}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Status</span>
            <span className={`${styles.statusBadge} ${enabled ? styles.statusEnabled : styles.statusDisabled}`}>
              {enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          {tags.length > 0 && (
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Tags</span>
              <div className={styles.tagList}>
                {tags.map((tag: any) => (
                  <span key={tag.name} className={styles.tag}>{tag.name}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 2. Request Mapping */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <ArrowRightLeft size={16} />
          <span className={styles.cardTitle}>{`2. ${connector?.connectionName || ''} Request Mapping`}</span>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Method</span>
            <span className={styles.methodBadge}>{httpMethod}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Path</span>
            <span className={styles.infoValueMono}>{path}</span>
          </div>
          {httpMethod !== 'GET' && (
            <>
              <button type="button" className={styles.jsonToggle} onClick={() => toggleJson('body')}>
                <ChevronDown size={14} className={expandedJson === 'body' ? styles.rotated : ''} />
                Body Template
              </button>
              {expandedJson === 'body' && (
                <pre className={styles.jsonBlock}>{watch('bodyTemplate') || '(empty)'}</pre>
              )}
            </>
          )}
        </div>
      </div>

      {/* 3. Input Fields */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <ListChecks size={16} />
          <span className={styles.cardTitle}>3. MCP Input Fields</span>
          <span className={styles.cardCount}>{fields.length}</span>
        </div>
        <div className={styles.cardBody}>
          {fields.length === 0 ? (
            <span className={styles.emptyText}>No input fields configured.</span>
          ) : (
            <div className={styles.fieldGrid}>
              {fields.map(field => (
                <div key={field.name} className={styles.fieldItem}>
                  <span className={styles.fieldName}>{field.label || field.name}</span>
                  <span className={styles.fieldMeta}>
                    {field.type}{field.required ? ' · Required' : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
          <button type="button" className={styles.jsonToggle} onClick={() => toggleJson('schema')}>
            <ChevronDown size={14} className={expandedJson === 'schema' ? styles.rotated : ''} />
            Input Schema JSON
          </button>
          {expandedJson === 'schema' && (
            <pre className={styles.jsonBlock}>{watch('inputSchema') || '{}'}</pre>
          )}
        </div>
      </div>

      {/* 4. Chat Quick Actions */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <MessageCircle size={16} />
          <span className={styles.cardTitle}>4. Chat Quick Actions</span>
        </div>
        <div className={styles.cardBody}>
          {chatConfig.promptSuggestions.filter(s => s.text.trim()).length > 0 ? (
            <div className={styles.promptSummary}>
              {chatConfig.promptSuggestions
                .filter(s => s.text.trim())
                .map((suggestion, idx) => (
                  <div key={idx} className={styles.promptItem}>
                    <span>"{suggestion.text}"</span>
                    {suggestion.requiredFields.length > 0 && (
                      <span className={styles.linkedFields}>
                        {suggestion.requiredFields.map(f => suggestion.labels[f] || f).join(', ')}
                      </span>
                    )}
                  </div>
                ))}
            </div>
          ) : (
            <span className={styles.emptyText}>No prompt suggestions configured.</span>
          )}
          {chatConfig.confirmBeforeExecute && (
            <span className={styles.confirmBadge}>Requires confirmation</span>
          )}
        </div>
      </div>

    </div>
  );
}
